import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  useTheme,
  Container 
} from '@mui/material';
import { HomeRounded, SearchRounded } from '@mui/icons-material';

export default function NotFoundPage() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)'
          : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={6}
          sx={{
            p: 6,
            textAlign: 'center',
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(0,0,0,0.05)',
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 4 }}>
            <Box 
              sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: '3rem' }}>💸</Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  fontWeight: 900,
                  color: 'text.primary',
                  fontSize: { xs: '2rem', sm: '2.5rem' },
                }}
              >
                cost.wtf
              </Typography>
            </Box>
          </Box>

          {/* 404 Icon */}
          <SearchRounded 
            sx={{ 
              fontSize: '4rem', 
              color: 'warning.main',
              mb: 3 
            }} 
          />

          {/* Error Message */}
          <Typography
            variant="h1"
            sx={{
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 900,
              color: 'text.primary',
              fontSize: { xs: '3rem', sm: '4rem' },
              mb: 2,
            }}
          >
            404
          </Typography>

          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 700,
              color: 'text.primary',
              mb: 2,
            }}
          >
            Page Not Found
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              mb: 4,
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            }}
          >
            Oops! This page doesn't exist. Don't worry, you're not being charged for this error!
            <br />
            Let's get you back to calculating some costs.
          </Typography>

          {/* Navigation Suggestions */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                mb: 2,
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              }}
            >
              Try one of these popular calculators:
            </Typography>
            
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'center',
                flexWrap: 'wrap',
                mb: 3 
              }}
            >
              <Button
                component={Link}
                to="/meetings"
                variant="outlined"
                sx={{
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  fontWeight: 600,
                }}
              >
                Meeting Calculator
              </Button>
              
              <Button
                component={Link}
                to="/live-meetings"
                variant="outlined"
                sx={{
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  fontWeight: 600,
                }}
              >
                Live Meeting Timer
              </Button>
              
              <Button
                component={Link}
                to="/documents"
                variant="outlined"
                sx={{
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  fontWeight: 600,
                }}
              >
                Document Calculator
              </Button>
            </Box>
          </Box>

          {/* Home Button */}
          <Button
            component={Link}
            to="/"
            variant="contained"
            size="large"
            startIcon={<HomeRounded />}
            sx={{
              px: 4,
              py: 1.5,
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 700,
              fontSize: '1.1rem',
            }}
          >
            Go to Homepage
          </Button>

          {/* Footer */}
          <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              }}
            >
              Lost? No problem! Every great calculator session starts with finding the right page.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}